/**
 * Purchase Orders (PO-01) — read ESC's PO history (mirror_po / mirror_po_led,
 * read-only DEC-22) AND create Pegasus-native POs (pegasus_po / pegasus_po_line,
 * the write-machinery we cut over to later). Vendor names resolve from
 * mirror_vendor. Consistent edit/slide-out pattern with the rest of Pegasus.
 */
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";

export interface EscPo {
  po: string; date: string | null; vendor_code: string | null; vendor_name?: string | null;
  status: string | null; billed: boolean; order_placed: boolean; ship_name: string | null; memo: string | null;
  buyer: string | null; terms: string | null; ship_via: string | null;
  dispatch: string | null; custno: string | null; locno: string | null; date_req: string | null;
}
export interface EscPoLine {
  po: string; counter: string; part: string | null; description: string | null;
  quan: number | null; price: number | null; amount: number | null; received: number | null;
  dispatch_no: string | null;
}
/** PO-03 (Seth 2026-09-03): the lifecycle is shaped like ESC's — Save (placed
 *  with the vendor) → Receive (per line, partial allowed) → Bill (vendor
 *  invoice entered). Draft exists only for "not called in yet". */
export type PoStatus = "draft" | "ordered" | "received" | "billed" | "cancelled";
export type PoOrderMethod = "phone" | "email" | "portal" | "counter" | "text";
export const PO_ORDER_METHODS: { value: PoOrderMethod; label: string }[] = [
  { value: "phone", label: "Phone" },
  { value: "email", label: "Email" },
  { value: "portal", label: "Vendor Website" },
  { value: "counter", label: "At the Counter" },
  { value: "text", label: "Text" },
];
export const PO_STATUS_LABEL: Record<PoStatus, string> = { draft: "Draft", ordered: "Ordered", received: "Received", billed: "Billed", cancelled: "Cancelled" };

export interface NativePo {
  id: string; po_number: string | null; vendor_code: string | null; vendor_name?: string | null;
  status: PoStatus; memo: string | null; terms: string | null; ship_to: string | null;
  ship_via: string | null; buyer: string | null; date_requested: string | null;
  custno: string | null; locno: string | null; dispatch_no: string | null; ship_to_addr: string | null;
  job_id: string | null; job_title?: string | null;
  created_by: string | null; created_at: string; total?: number;
  /** Resolved for the list view — "who is this part FOR", human-readable. */
  cust_name?: string | null;
  // PO-03 tracking — who placed it, how, when it's due, who received / billed it.
  ordered_at: string | null; ordered_by: string | null; order_method: PoOrderMethod | null; vendor_order_ref: string | null;
  expected_date: string | null;
  received_at: string | null; received_by: string | null; received_note: string | null;
  vendor_invoice_no: string | null; vendor_invoice_date: string | null; vendor_invoice_amount: number | null;
  billed_at: string | null; billed_by: string | null;
  cancelled_at: string | null; cancelled_by: string | null;
  /** PARTS-EQUIP-01: the part ticket this PO orders for, and the unit the part is for. */
  ticket_id?: string | null; equipment?: PartEquipment | null;
}
export interface NativePoLineInput { part_number: string | null; description: string | null; qty: number; unit_cost: number }
export interface NativePoLine extends NativePoLineInput { id: string; qty_received: number }

/** ESC's dropdown values — kept identical so the office isn't relearning. */
export const PO_TERMS = ["COD", "NET 10", "NET 14", "NET 30", "NET 60", "NET 90"];
export const PO_SHIP_VIA = ["Courier", "Deliver", "NDA", "Speedy", "UPS", "Will Call"];
export const SEDGWICK_SHIP_TO = "Sedgwick Heating & AC\n1240 Trapp Road, Suite A\nEagan, MN 55121";

/** TICKET-EQUIP-01: the unit a part is for, as the ticket / PO carries it. */
export interface PartEquipment {
  counter?: string | null; mfg: string | null; model: string | null; serial: string | null;
  eq_type: string | null; equip_loc?: string | null; install_date: string | null; src?: "esc" | "peg";
}
export function equipmentLabel(e: PartEquipment | null | undefined): string {
  if (!e) return "";
  return [e.mfg, e.model].filter(Boolean).join(" ") || e.eq_type || "Unit on file";
}

/** Equipment on file at a location (field-added units first, then ESC's file,
 *  systems first) — so the parts manager can see model + serial even when the
 *  tech didn't pick a unit. */
export function useLocationEquipment(custno: string | null, locno: string | null) {
  return useQuery({
    queryKey: ["po_location_equipment", custno, locno],
    enabled: !!custno,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<PartEquipment[]> => {
      let q = supabase.from("mirror_equipment")
        .select("counter, mfg, model, serial, eq_type, equip_loc, install_date")
        .eq("custno", custno!)
        .not("model", "is", null);
      if (locno) q = q.eq("locno", locno);
      // PARTS-EQUIP-01: units added in the field / by the office live in pegasus_equipment.
      let p = supabase.from("pegasus_equipment")
        .select("id, brand, model, serial, category, equip_loc, install_date, status")
        .eq("custno", custno!);
      if (locno) p = p.eq("locno", locno);
      const [{ data }, peg] = await Promise.all([
        q.order("install_date", { ascending: false, nullsFirst: false }).limit(40),
        p.order("created_at", { ascending: false }).limit(12),
      ]);
      const pegRows: PartEquipment[] = ((peg.data ?? []) as Array<{ id: string; brand: string | null; model: string | null; serial: string | null; category: string | null; equip_loc: string | null; install_date: string | null; status: string | null }>)
        .filter((r) => (r.status ?? "active") === "active")
        .map((r) => ({ counter: `peg:${r.id}`, mfg: r.brand, model: r.model, serial: r.serial, eq_type: r.category, equip_loc: r.equip_loc, install_date: r.install_date, src: "peg" as const }));
      const rows = ((data ?? []) as PartEquipment[]).filter((r) => (r.model ?? "").trim().length > 0);
      // Real units carry a serial or an equipment type; bare install materials don't.
      const units = rows.filter((r) => (r.serial ?? "").trim() || (r.eq_type ?? "").trim());
      const seen = new Set<string>();
      const out: PartEquipment[] = [];
      for (const r of [...pegRows, ...(units.length ? units : rows)]) {
        const k = `${(r.serial ?? "").toUpperCase().replace(/[^A-Z0-9]/g, "")}|${(r.model ?? "").toUpperCase()}`;
        if (seen.has(k)) continue;
        seen.add(k);
        out.push(r.src === "peg" ? r : { ...r, src: "esc" });
      }
      return out.slice(0, 12);
    },
  });
}

/** PARTS-EQUIP-01 (Seth 2026-09-10: "from the today tab enter PO be able to
 *  manually add equipment"): a unit that isn't on file yet, added by the office
 *  while ordering — lands in pegasus_equipment at the address, same as a unit a
 *  tech adds from the truck, and comes back shaped for the PO / ticket. */
export function useAddUnit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { custno: string; locno: string | null; category: string | null; brand: string | null; model: string | null; serial: string | null; equip_loc: string | null; origin_dispatch?: string | null }): Promise<PartEquipment> => {
      const { data: u } = await supabase.auth.getUser();
      const { data, error } = await supabase.from("pegasus_equipment").insert({
        custno: v.custno, locno: v.locno, category: v.category, brand: v.brand, model: v.model,
        serial: v.serial ? v.serial.toUpperCase() : null, equip_loc: v.equip_loc,
        origin_dispatch: v.origin_dispatch ?? null, source: "office", created_by: u?.user?.email ?? null,
      }).select("id").single();
      if (error) throw error;
      return { counter: `peg:${(data as { id: string }).id}`, mfg: v.brand, model: v.model, serial: v.serial ? v.serial.toUpperCase() : null, eq_type: v.category, equip_loc: v.equip_loc, install_date: null, src: "peg" };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["po_location_equipment"] });
      qc.invalidateQueries({ queryKey: ["installed_equipment"] });
      qc.invalidateQueries({ queryKey: ["m_equipment"] });
    },
  });
}

/** Vendor card for the PO detail — phone + account + address (the billing
 *  address lives on the vendor record, not on the PO). */
export function useVendorCard(code: string | null) {
  return useQuery({
    queryKey: ["po_vendor_card", code],
    enabled: !!code,
    staleTime: 10 * 60 * 1000,
    queryFn: async () => {
      const { data } = await supabase.from("mirror_vendor")
        .select("vendor_code, name, phone, phone2, email, account, terms, address1, address2, city, state, zip, web_url")
        .eq("vendor_code", code!).maybeSingle();
      return (data ?? null) as { vendor_code: string; name: string | null; phone: string | null; phone2: string | null; email: string | null; account: string | null; terms: string | null; address1: string | null; address2: string | null; city: string | null; state: string | null; zip: string | null; web_url: string | null } | null;
    },
  });
}

export interface CustomerLite { custno: string; locno: string; name: string; address: string | null }
/** Search a customer/address to ATTACH a PO to a job (feeds invoicing later). */
export function useCustomerLookup(term: string) {
  return useQuery({
    queryKey: ["po_customer_lookup", term.trim().toLowerCase()],
    enabled: term.trim().length >= 2,
    staleTime: 30 * 1000,
    queryFn: async (): Promise<CustomerLite[]> => {
      const t = term.trim();
      const { data } = await supabase.from("mirror_location")
        .select("custno, locno, locname, add1, city")
        .or(`locname.ilike.%${t}%,add1.ilike.%${t}%`)
        .limit(15);
      return (data ?? []).map((l: { custno: string; locno: string; locname: string | null; add1: string | null; city: string | null }) => ({
        custno: l.custno, locno: l.locno, name: l.locname ?? l.custno,
        address: `${l.add1 ?? ""}${l.city ? ", " + l.city : ""}`,
      }));
    },
  });
}

async function vendorNameMap(codes: string[]): Promise<Map<string, string | null>> {
  const uniq = [...new Set(codes.filter(Boolean))] as string[];
  if (uniq.length === 0) return new Map();
  const { data } = await supabase.from("mirror_vendor").select("vendor_code,name").in("vendor_code", uniq);
  return new Map((data ?? []).map((v: { vendor_code: string; name: string | null }) => [v.vendor_code, v.name]));
}

const PO_COLS = "po,date,vendor_code,status,billed,order_placed,ship_name,memo,buyer,terms,ship_via,dispatch,custno,locno,date_req";

/** PO-02 (Seth 2026-08-31: "search for customer or address or memo… part
 *  number or anything like that"): one box finds POs by PO #, vendor, ship-to,
 *  MEMO, buyer, PART NUMBER / PART DESCRIPTION (through the lines), and the
 *  CUSTOMER's name or address (through the account link). */
export function useEscPoList(search: string) {
  return useQuery({
    queryKey: ["esc_po_list", search.trim().toLowerCase()],
    staleTime: 60 * 1000,
    queryFn: async (): Promise<EscPo[]> => {
      const s = search.trim().replace(/[%_,()]/g, " ").replace(/\s+/g, " ").trim();
      if (!s) {
        const { data, error } = await supabase.from("mirror_po").select(PO_COLS)
          .order("date", { ascending: false, nullsFirst: false }).limit(200);
        if (error) throw error;
        const rows = (data ?? []) as EscPo[];
        const names = await vendorNameMap(rows.map((r) => r.vendor_code ?? ""));
        return rows.map((r) => ({ ...r, vendor_name: r.vendor_code ? names.get(r.vendor_code) ?? null : null }));
      }
      const pat = `%${s}%`;
      const [byHeader, byLines, byCust] = await Promise.all([
        supabase.from("mirror_po").select(PO_COLS)
          .or(`po.ilike.${pat},vendor_code.ilike.${pat},ship_name.ilike.${pat},memo.ilike.${pat},buyer.ilike.${pat}`)
          .order("date", { ascending: false, nullsFirst: false }).limit(120),
        supabase.from("mirror_po_led").select("po").or(`part.ilike.${pat},description.ilike.${pat}`).limit(300),
        s.length >= 3
          ? supabase.from("mirror_location").select("custno").or(`locname.ilike.${pat},add1.ilike.${pat}`).limit(60)
          : Promise.resolve({ data: [] as { custno: string }[] }),
      ]);
      const merged = new Map<string, EscPo>();
      for (const r of (byHeader.data ?? []) as EscPo[]) merged.set(r.po, r);
      const linePos = [...new Set(((byLines.data ?? []) as { po: string }[]).map((l) => l.po))].filter((p) => !merged.has(p)).slice(0, 150);
      if (linePos.length) {
        const { data } = await supabase.from("mirror_po").select(PO_COLS).in("po", linePos);
        for (const r of (data ?? []) as EscPo[]) merged.set(r.po, r);
      }
      const custnos = [...new Set(((byCust.data ?? []) as { custno: string }[]).map((c) => c.custno))].slice(0, 50);
      if (custnos.length) {
        const { data } = await supabase.from("mirror_po").select(PO_COLS).in("custno", custnos).limit(120);
        for (const r of (data ?? []) as EscPo[]) if (!merged.has(r.po)) merged.set(r.po, r);
      }
      const rows = [...merged.values()].sort((a, b) => String(b.date ?? "").localeCompare(String(a.date ?? ""))).slice(0, 200);
      const names = await vendorNameMap(rows.map((r) => r.vendor_code ?? ""));
      return rows.map((r) => ({ ...r, vendor_name: r.vendor_code ? names.get(r.vendor_code) ?? null : null }));
    },
  });
}

export function useEscPoLines(po: string | null) {
  return useQuery({
    queryKey: ["esc_po_lines", po],
    enabled: !!po,
    queryFn: async (): Promise<EscPoLine[]> => {
      const { data } = await supabase.from("mirror_po_led")
        .select("po,counter,part,description,quan,price,amount,received,dispatch_no").eq("po", po!).order("counter");
      return (data ?? []) as EscPoLine[];
    },
  });
}

export interface JobMatchingPo {
  source: "esc" | "pegasus";
  id: string;
  po_number: string | null;
  vendor: string | null;
  date: string | null;
  dispatch: string | null;
  matchedBy: "dispatch" | "property";
}

/**
 * POs that look like they belong to a job — matched to the job's originating
 * dispatch # (strongest) or its property/location (address proxy). Lets the job
 * flag "there's a PO to import" instead of the office hunting for it. Native POs
 * already linked to this job are excluded (they're not something to re-import).
 */
export function useJobMatchingPos(
  custno: string | null,
  locno: string | null,
  dispatchNo: string | null,
  jobId: string | null,
) {
  return useQuery({
    queryKey: ["job_matching_pos", custno, locno, dispatchNo, jobId],
    enabled: !!custno,
    staleTime: 60 * 1000,
    queryFn: async (): Promise<JobMatchingPo[]> => {
      const disp = (dispatchNo ?? "").trim();
      const [{ data: esc }, { data: nat }] = await Promise.all([
        supabase.from("mirror_po")
          .select("po,date,vendor_code,dispatch,custno,locno")
          .eq("custno", custno!).order("date", { ascending: false, nullsFirst: false }).limit(100),
        supabase.from("pegasus_po")
          .select("id,po_number,vendor_code,date_requested,dispatch_no,custno,locno,job_id")
          .eq("custno", custno!).limit(100),
      ]);
      const escRows = (esc ?? []) as { po: string; date: string | null; vendor_code: string | null; dispatch: string | null; custno: string | null; locno: string | null }[];
      const natRows = (nat ?? []) as { id: string; po_number: string | null; vendor_code: string | null; date_requested: string | null; dispatch_no: string | null; custno: string | null; locno: string | null; job_id: string | null }[];
      const vmap = await vendorNameMap([...escRows.map((r) => r.vendor_code ?? ""), ...natRows.map((r) => r.vendor_code ?? "")]);
      const out: JobMatchingPo[] = [];
      for (const r of escRows) {
        const byDispatch = !!disp && (r.dispatch ?? "").trim() === disp;
        const byProperty = !!locno && (r.locno ?? "") === locno;
        if (byDispatch || byProperty) {
          out.push({ source: "esc", id: r.po, po_number: r.po, vendor: r.vendor_code ? vmap.get(r.vendor_code) ?? r.vendor_code : null, date: r.date, dispatch: r.dispatch, matchedBy: byDispatch ? "dispatch" : "property" });
        }
      }
      for (const r of natRows) {
        if (r.job_id === jobId) continue;
        const byDispatch = !!disp && (r.dispatch_no ?? "").trim() === disp;
        const byProperty = !!locno && (r.locno ?? "") === locno;
        if (byDispatch || byProperty) {
          out.push({ source: "pegasus", id: r.id, po_number: r.po_number, vendor: r.vendor_code ? vmap.get(r.vendor_code) ?? r.vendor_code : null, date: r.date_requested, dispatch: r.dispatch_no, matchedBy: byDispatch ? "dispatch" : "property" });
        }
      }
      return out.sort((a, b) => (a.matchedBy === "dispatch" ? 0 : 1) - (b.matchedBy === "dispatch" ? 0 : 1));
    },
  });
}

export function useNativePos() {
  return useQuery({
    queryKey: ["native_pos"],
    staleTime: 15 * 1000,
    queryFn: async (): Promise<NativePo[]> => {
      const { data } = await supabase.from("pegasus_po").select("*").order("created_at", { ascending: false });
      const rows = (data ?? []) as NativePo[];
      if (rows.length === 0) return [];
      const jobIds = [...new Set(rows.map((r) => r.job_id).filter(Boolean))] as string[];
      const [{ data: lines }, names, { data: jobs }] = await Promise.all([
        supabase.from("pegasus_po_line").select("po_id, qty, unit_cost").in("po_id", rows.map((r) => r.id)),
        vendorNameMap(rows.map((r) => r.vendor_code ?? "")),
        jobIds.length ? supabase.from("pegasus_job").select("id,title").in("id", jobIds) : Promise.resolve({ data: [] as { id: string; title: string | null }[] }),
      ]);
      const jobTitle = new Map((jobs ?? []).map((j: { id: string; title: string | null }) => [j.id, j.title]));
      const totals = new Map<string, number>();
      for (const l of (lines ?? []) as { po_id: string; qty: number; unit_cost: number }[]) {
        totals.set(l.po_id, (totals.get(l.po_id) ?? 0) + Number(l.qty || 0) * Number(l.unit_cost || 0));
      }
      // "For job" shows a NAME, never a bare customer number (Seth 2026-08-31).
      const custnos = [...new Set(rows.map((r) => r.custno).filter(Boolean))] as string[];
      const custName = new Map<string, string>();
      if (custnos.length) {
        const { data: locs } = await supabase.from("mirror_location")
          .select("custno, locno, locname").in("custno", custnos).order("locno");
        for (const l of (locs ?? []) as { custno: string; locname: string | null }[]) {
          if (l.locname && !custName.has(l.custno)) custName.set(l.custno, l.locname);
        }
      }
      return rows.map((r) => ({
        ...r, total: totals.get(r.id) ?? 0,
        vendor_name: r.vendor_code ? names.get(r.vendor_code) ?? null : null,
        job_title: r.job_id ? jobTitle.get(r.job_id) ?? null : null,
        cust_name: r.custno ? custName.get(r.custno) ?? null : null,
      }));
    },
  });
}

export function useNativePoLines(poId: string | null) {
  return useQuery({
    queryKey: ["native_po_lines", poId],
    enabled: !!poId,
    queryFn: async () => {
      const { data } = await supabase.from("pegasus_po_line").select("*").eq("po_id", poId!).order("sort");
      return (data ?? []) as NativePoLine[];
    },
  });
}

export interface NativePoHeaderInput {
  vendor_code: string | null; terms?: string | null; ship_via?: string | null;
  ship_to_addr?: string | null; date_requested?: string | null; expected_date?: string | null;
  order_method?: PoOrderMethod | null; vendor_order_ref?: string | null;
  custno?: string | null; locno?: string | null; job_id?: string | null; memo?: string;
  dispatch_no?: string | null;
  /** PARTS-EQUIP-01: the part ticket this PO is for + the unit the part is for. */
  ticket_id?: string | null; equipment?: PartEquipment | null;
}

export function useCreateNativePo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: NativePoHeaderInput & {
      /** DISPATCH-NO-02: the origin Pegasus call's internal ref — used to flip
       *  its part ticket even though the FIELD now shows the real call number. */
      origin_ref?: string | null;
      lines: NativePoLineInput[];
      /** PO-03: "Save" = placed with the vendor (ordered). "Save as draft" = not called in yet. */
      draft?: boolean;
      /** "Save & Receive" — the parts are in hand right now (counter pickup). */
      receive?: boolean;
    }) => {
      const { data: u } = await supabase.auth.getUser();
      const email = u?.user?.email ?? null;
      const now = new Date().toISOString();
      const placed = !v.draft;
      // po_number auto-fills from the pegasus_po_seq default
      const { data: po, error } = await supabase.from("pegasus_po")
        .insert({
          vendor_code: v.vendor_code, terms: v.terms ?? null, ship_via: v.ship_via ?? null,
          ship_to_addr: v.ship_to_addr ?? null, date_requested: v.date_requested ?? null,
          expected_date: v.expected_date ?? null,
          order_method: placed ? (v.order_method ?? null) : null, vendor_order_ref: v.vendor_order_ref?.trim() || null,
          custno: v.custno ?? null, locno: v.locno ?? null, job_id: v.job_id ?? null, memo: v.memo ?? null,
          dispatch_no: v.dispatch_no?.trim() || null,
          ticket_id: v.ticket_id ?? null, equipment: v.equipment ?? null,
          status: placed ? "ordered" : "draft",
          ordered_at: placed ? now : null, ordered_by: placed ? email : null,
          buyer: email, created_by: email,
        })
        .select().single();
      if (error) throw error;
      const lines = v.lines.filter((l) => l.part_number || l.description)
        .map((l, i) => ({ po_id: po.id, part_number: l.part_number, description: l.description, qty: l.qty, unit_cost: l.unit_cost, sort: i }));
      if (lines.length > 0) {
        const { error: le } = await supabase.from("pegasus_po_line").insert(lines);
        if (le) throw le;
      }
      if (v.receive) {
        const { error: re } = await supabase.rpc("receive_po", { p_po_id: po.id });
        if (re) throw re;
      }
      // TICKET-PO-01 (Seth 2026-08-26): cutting a PO for a dispatch ADVANCES its
      // open part ticket(s) automatically — "Needs ordering" flips to "On order"
      // with the PO number + expected date stamped on the ticket. Best-effort:
      // a miss here never fails the PO (the office can still mark it by hand).
      const originKey = v.origin_ref?.trim() || v.dispatch_no?.trim();
      if ((originKey || v.ticket_id) && placed) {
        try {
          const stamp = {
            part_state: v.receive ? "arrived" : "ordered",
            part_po: (po as NativePo).po_number,
            part_eta: v.expected_date ?? v.date_requested ?? null,
            ordered_at: now,
            ordered_by: email,
            ...(v.receive ? { arrived_at: now, arrived_by: email } : {}),
            updated_at: now,
          };
          let q = supabase.from("pegasus_dispatch").update(stamp)
            .eq("lane_key", "order_parts")
            .eq("status", "pending_schedule")
            .in("part_state", ["needs_ordering", "backordered"]);
          // PARTS-EQUIP-01: the ticket itself when we have it; else every open
          // part ticket born on the origin call.
          q = v.ticket_id ? q.eq("id", v.ticket_id) : q.eq("origin_dispatch", originKey!);
          await q;
        } catch { /* never block the PO on the ticket bump */ }
      }
      return po as NativePo;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["native_pos"] });
      qc.invalidateQueries({ queryKey: ["items"] });
      qc.invalidateQueries({ queryKey: ["pending_tickets"] });
      qc.invalidateQueries({ queryKey: ["pending_tickets_customer"] });
    },
  });
}

/**
 * Edit a DRAFT Pegasus PO — update the header + replace its lines. Draft-only is
 * enforced in the UI (a PO that's been ordered/received is locked so the
 * received inventory can't desync from the line it was posted against).
 */
export function useUpdateNativePo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: NativePoHeaderInput & { id: string; lines: NativePoLineInput[] }) => {
      const { error } = await supabase.from("pegasus_po").update({
        vendor_code: v.vendor_code, terms: v.terms ?? null, ship_via: v.ship_via ?? null,
        ship_to_addr: v.ship_to_addr ?? null, date_requested: v.date_requested ?? null,
        expected_date: v.expected_date ?? null, order_method: v.order_method ?? null, vendor_order_ref: v.vendor_order_ref?.trim() || null,
        custno: v.custno ?? null, locno: v.locno ?? null, job_id: v.job_id ?? null, memo: v.memo ?? null,
        dispatch_no: v.dispatch_no?.trim() || null,
        ...(v.equipment !== undefined ? { equipment: v.equipment } : {}),
        ...(v.ticket_id !== undefined ? { ticket_id: v.ticket_id } : {}),
        updated_at: new Date().toISOString(),
      }).eq("id", v.id);
      if (error) throw error;
      // Replace the lines wholesale — simplest correct edit for a draft.
      const { error: de } = await supabase.from("pegasus_po_line").delete().eq("po_id", v.id);
      if (de) throw de;
      const lines = v.lines.filter((l) => l.part_number || l.description)
        .map((l, i) => ({ po_id: v.id, part_number: l.part_number, description: l.description, qty: l.qty, unit_cost: l.unit_cost, sort: i }));
      if (lines.length > 0) {
        const { error: le } = await supabase.from("pegasus_po_line").insert(lines);
        if (le) throw le;
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["native_pos"] });
      qc.invalidateQueries({ queryKey: ["native_po_lines"] });
    },
  });
}

/** Status moves that aren't receiving: place a draft (ordered), cancel. Stamps who/when. */
export function useUpdateNativePoStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { id: string; status: PoStatus; order_method?: PoOrderMethod | null; vendor_order_ref?: string | null; expected_date?: string | null }) => {
      const { data: u } = await supabase.auth.getUser();
      const email = u?.user?.email ?? null;
      const now = new Date().toISOString();
      const patch: Record<string, unknown> = { status: v.status, updated_at: now };
      if (v.status === "ordered") {
        patch.ordered_at = now; patch.ordered_by = email;
        if (v.order_method !== undefined) patch.order_method = v.order_method;
        if (v.vendor_order_ref !== undefined) patch.vendor_order_ref = v.vendor_order_ref?.trim() || null;
        if (v.expected_date !== undefined) patch.expected_date = v.expected_date;
      }
      if (v.status === "cancelled") { patch.cancelled_at = now; patch.cancelled_by = email; }
      const { error } = await supabase.from("pegasus_po").update(patch).eq("id", v.id);
      if (error) throw error;
      // Placing a draft flips the waiting ticket to On order, same as creating it placed.
      if (v.status === "ordered") {
        try {
          const { data: po } = await supabase.from("pegasus_po").select("po_number, dispatch_no, expected_date, date_requested").eq("id", v.id).maybeSingle();
          const pr = po as { po_number: string | null; dispatch_no: string | null; expected_date: string | null; date_requested: string | null } | null;
          if (pr?.dispatch_no) {
            await supabase.from("pegasus_dispatch")
              .update({ part_state: "ordered", part_po: pr.po_number, part_eta: pr.expected_date ?? pr.date_requested ?? null, ordered_at: now, ordered_by: email, updated_at: now })
              .eq("lane_key", "order_parts").eq("status", "pending_schedule")
              .eq("origin_dispatch", pr.dispatch_no).in("part_state", ["needs_ordering", "backordered"]);
          }
        } catch { /* never block the PO on the ticket bump */ }
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["native_pos"] });
      qc.invalidateQueries({ queryKey: ["pending_tickets"] });
      qc.invalidateQueries({ queryKey: ["pending_tickets_customer"] });
    },
  });
}

/**
 * Receive a PO — closes the loop: logs a pegasus_purchase per received line,
 * bumps the item's on-hand + last cost, and marks the PO received once every
 * line is complete (receive_po v2, atomic; partial receiving keeps it Ordered
 * with "3 of 5 received" on the line). Those purchases then flow back into the
 * item's history + cost-compare. Test-data machinery for now; live at
 * purchasing cutover (DEC-25).
 */
export function useReceivePo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: string | { poId: string; lines?: { id: string; qty: number }[] | null; note?: string | null }) => {
      const poId = typeof v === "string" ? v : v.poId;
      const lines = typeof v === "string" ? null : v.lines ?? null;
      const note = typeof v === "string" ? null : v.note ?? null;
      const { data, error } = await supabase.rpc("receive_po", { p_po_id: poId, p_lines: lines, p_note: note });
      if (error) throw error;
      const result = (data ?? {}) as { complete?: boolean; received_any?: boolean };
      // PARTS loop closure (Seth 2026-08-28): receiving the PO marks the part
      // ARRIVED on any ticket waiting on it — Today + the board strip flip to
      // "book the return visit" with zero extra clicks. Best-effort: a miss
      // here never fails the receive. A partial receive still counts — the
      // part the tech needs is usually the one that came in.
      if (result.received_any !== false) {
        try {
          const { data: u } = await supabase.auth.getUser();
          const { data: po } = await supabase.from("pegasus_po").select("po_number, dispatch_no").eq("id", poId).maybeSingle();
          const pr = po as { po_number: string | null; dispatch_no: string | null } | null;
          if (pr?.dispatch_no || pr?.po_number) {
            const now = new Date().toISOString();
            let q = supabase.from("pegasus_dispatch")
              .update({ part_state: "arrived", arrived_at: now, arrived_by: u?.user?.email ?? null, updated_at: now })
              .eq("lane_key", "order_parts")
              .eq("status", "pending_schedule")
              .in("part_state", ["ordered", "backordered", "needs_ordering"]);
            q = pr.dispatch_no ? q.eq("origin_dispatch", pr.dispatch_no) : q.eq("part_po", pr.po_number!);
            await q;
          }
        } catch { /* the ticket bump never blocks receiving */ }
      }
      return result;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["native_pos"] });
      qc.invalidateQueries({ queryKey: ["native_po_lines"] });
      qc.invalidateQueries({ queryKey: ["items"] });
      qc.invalidateQueries({ queryKey: ["item_purchase_history"] });
      qc.invalidateQueries({ queryKey: ["part_vendors"] });
      qc.invalidateQueries({ queryKey: ["pending_tickets"] });
      qc.invalidateQueries({ queryKey: ["pending_tickets_customer"] });
      qc.invalidateQueries({ queryKey: ["parts_pickup"] });
    },
  });
}

/**
 * Bill a PO (ESC's "Save & Bill" / "Bill" step) — record the vendor's invoice
 * against it. Pegasus doesn't run payables; this is the job-cost record and
 * the bookkeeper's cue that the bill exists.
 */
export function useBillPo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { id: string; invoice_no: string; invoice_date: string | null; amount: number | null }) => {
      const { data: u } = await supabase.auth.getUser();
      const now = new Date().toISOString();
      const { error } = await supabase.from("pegasus_po").update({
        status: "billed", vendor_invoice_no: v.invoice_no.trim() || null, vendor_invoice_date: v.invoice_date,
        vendor_invoice_amount: v.amount, billed_at: now, billed_by: u?.user?.email ?? null, updated_at: now,
      }).eq("id", v.id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["native_pos"] }),
  });
}
